import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ComboBox } from "../../../../../components/combo-box";
import type { ComboBoxItem } from "../../../../../components/combo-box";
import { Dropdown } from "../../../../../components/dropdown";
import Papa from "papaparse";
import ImageUploader from "../../misc/ImageUploader";
import Swal from "sweetalert2";
import {
  Calendar,
  ChevronLeft,
  FileDown,
  FileSpreadsheet,
  Hash,
  Plus,
  Send,
  ChevronDown,
  Trash2,
  Upload,
  X,
} from "lucide-react";

const VENTA_WIZARD_STEPS = [
  { id: 0, label: "Factura y comprobante" },
  { id: 1, label: "Productos" },
  { id: 2, label: "Resumen" },
] as const;

interface Props {
  view: boolean;
  setView: React.Dispatch<React.SetStateAction<boolean>>;
}

interface Producto {
  producto_id: number;
  nombre: string;
  articulo: string;
  cantidad: number;
  unidad: string;
  cantidad_por_caja: number;
}

interface VentaComprobante {
  numero_factura: number;
  fecha: Date;
}

const BRAND = "#205f43";
const BRAND_SOFT = "rgba(32, 95, 67, 0.08)";

function createEmptyTempProducto(): Producto {
  return { unidad: "Caja" } as Producto;
}

function normalizeCsvHeader(h: string): string {
  return String(h ?? "").replace(/^\uFEFF/, "").trim();
}

/** Read cell by canonical column name (case-insensitive header). */
function getCsvCell(
  row: Record<string, unknown>,
  canonicalHeader: string
): string {
  const want = canonicalHeader.toLowerCase();
  for (const [key, val] of Object.entries(row)) {
    if (normalizeCsvHeader(key).toLowerCase() === want) {
      if (val == null) return "";
      return String(val).trim();
    }
  }
  return "";
}

function normalizeUnidadCsv(raw: string): "Caja" | "Unidad" | null {
  const t = raw.trim();
  if (!t) return null;
  const lower = t.toLowerCase();
  if (lower === "caja") return "Caja";
  if (lower === "unidad") return "Unidad";
  return null;
}

type CsvRowUpdate = { articulo: string; unidad: "Caja" | "Unidad"; cantidad: number };

function validateCsvHeaders(fields: string[] | undefined): string | null {
  if (!fields?.length) {
    return "No se pudieron leer las columnas del CSV. La primera fila debe incluir Articulo, Unidad y Cantidad.";
  }
  const n = fields.map((f) => normalizeCsvHeader(f).toLowerCase());
  if (!n.includes("articulo")) return 'Falta la columna "Articulo".';
  if (!n.includes("unidad")) return 'Falta la columna "Unidad".';
  if (!n.includes("cantidad")) return 'Falta la columna "Cantidad".';
  return null;
}

function validateCsvProductRows(
  rows: Record<string, unknown>[],
  productList: Producto[]
): { ok: true; updates: CsvRowUpdate[] } | { ok: false; messages: string[] } {
  const messages: string[] = [];
  const updates: CsvRowUpdate[] = [];

  if (rows.length === 0) {
    return { ok: false, messages: ["El archivo no contiene filas de datos."] };
  }

  rows.forEach((row, i) => {
    const rowNum = i + 2;
    const articulo = getCsvCell(row, "articulo");
    const unidadRaw = getCsvCell(row, "unidad");
    const cantidadRaw = getCsvCell(row, "cantidad");

    const anyFilled =
      articulo !== "" || unidadRaw !== "" || cantidadRaw !== "";
    if (!anyFilled) return;

    const rowErrors: string[] = [];

    if (!articulo) {
      rowErrors.push(`Fila ${rowNum}: Artículo es obligatorio.`);
    }
    if (!unidadRaw) {
      rowErrors.push(`Fila ${rowNum}: Unidad es obligatoria.`);
    }
    if (!cantidadRaw) {
      rowErrors.push(`Fila ${rowNum}: Cantidad es obligatoria.`);
    }

    let cantidad = 0;
    const qtyStr = String(cantidadRaw).trim().replace(/\s/g, "");
    if (cantidadRaw) {
      if (!/^\d+$/.test(qtyStr)) {
        rowErrors.push(
          `Fila ${rowNum}: Cantidad debe ser un número entero mayor o igual a 1.`
        );
      } else {
        cantidad = parseInt(qtyStr, 10);
        if (!Number.isFinite(cantidad) || cantidad < 1) {
          rowErrors.push(
            `Fila ${rowNum}: Cantidad debe ser un número entero mayor o igual a 1.`
          );
        }
      }
    }

    const unidad = normalizeUnidadCsv(unidadRaw);
    if (unidadRaw && !unidad) {
      rowErrors.push(
        `Fila ${rowNum}: Unidad debe ser "Caja" o "Unidad" (valor recibido: "${unidadRaw}").`
      );
    }

    if (articulo && !productList.some((p) => p.articulo === articulo)) {
      rowErrors.push(
        `Fila ${rowNum}: el artículo "${articulo}" no está en tu catálogo de productos.`
      );
    }

    if (rowErrors.length > 0) {
      messages.push(...rowErrors);
      return;
    }

    if (!unidad) {
      messages.push(`Fila ${rowNum}: Unidad no reconocida.`);
      return;
    }

    updates.push({ articulo, unidad, cantidad });
  });

  if (updates.length === 0 && messages.length === 0) {
    return {
      ok: false,
      messages: [
        "No hay filas con datos. Cada fila debe incluir Articulo, Unidad y Cantidad.",
      ],
    };
  }

  if (messages.length > 0) {
    return { ok: false, messages };
  }

  return { ok: true, updates };
}

function mergeCsvUpdatesIntoCart(
  selectedProducts: Producto[],
  updates: CsvRowUpdate[],
  productList: Producto[]
): Producto[] {
  const updatedProducts = [...selectedProducts];
  updates.forEach((update) => {
    const productIndex = updatedProducts.findIndex(
      (p) => p.articulo === update.articulo && p.unidad === update.unidad
    );

    if (productIndex > -1) {
      const sum = updatedProducts[productIndex].cantidad + update.cantidad;
      updatedProducts[productIndex] = {
        ...updatedProducts[productIndex],
        cantidad: sum,
      };
    } else {
      const fullProductDetails = productList.find(
        (p) => p.articulo === update.articulo
      );
      if (fullProductDetails) {
        updatedProducts.push({
          ...fullProductDetails,
          cantidad: update.cantidad,
          unidad: update.unidad,
        });
      }
    }
  });
  return updatedProducts;
}

const VentaComprobanteModal = ({ view, setView }: Props) => {
  const [venta, setVenta] = useState<VentaComprobante>({
    numero_factura: 0,
    fecha: new Date(0),
  });
  const [tempProducto, setTempProducto] = useState<Producto>(
    createEmptyTempProducto()
  );
  const [resetProductoCombo, setResetProductoCombo] = useState(false);
  const [productList, setProductList] = useState<Producto[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Producto[]>([]);
  const [isClosing, setIsClosing] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File>();
  const [csvReportOpen, setCsvReportOpen] = useState(false);
  /** 0 = factura/comprobante, 1 = productos+CSV, 2 = resumen antes de enviar */
  const [wizardStep, setWizardStep] = useState(0);

  const handleCSVUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const input = event.target;
    if (!file) return;

    Papa.parse<Record<string, unknown>>(file, {
      header: true,
      skipEmptyLines: "greedy",
      transformHeader: (h) => normalizeCsvHeader(h),
      complete: (results) => {
        if (results.errors.length > 0) {
          const sample = results.errors
            .slice(0, 5)
            .map((e) => e.message || String(e.type))
            .join(" · ");
          void Swal.fire({
            icon: "error",
            title: "Error al leer el CSV",
            text: sample,
          });
          input.value = "";
          return;
        }

        const headerErr = validateCsvHeaders(results.meta?.fields);
        if (headerErr) {
          void Swal.fire({
            icon: "error",
            title: "CSV no válido",
            text: headerErr,
          });
          input.value = "";
          return;
        }

        const rows = (results.data ?? []).filter(
          (row) => row && typeof row === "object"
        ) as Record<string, unknown>[];

        const validated = validateCsvProductRows(rows, productList);
        if (!validated.ok) {
          const maxLines = 18;
          const lines = validated.messages.slice(0, maxLines);
          const more =
            validated.messages.length > maxLines
              ? `<p class="mt-2 text-sm">…y ${
                  validated.messages.length - maxLines
                } más.</p>`
              : "";
          void Swal.fire({
            icon: "error",
            title: "Revise el CSV",
            html: `<ul class="text-left text-sm" style="max-height:16rem;overflow:auto">${lines
              .map((m) => `<li>${m}</li>`)
              .join("")}</ul>${more}`,
          });
          input.value = "";
          return;
        }

        setSelectedProducts((prev) =>
          mergeCsvUpdatesIntoCart(prev, validated.updates, productList)
        );
        void Swal.fire({
          icon: "success",
          title: "Productos importados",
          text: `${validated.updates.length} línea(s) agregadas al listado.`,
          showConfirmButton: false,
          timer: 1800,
        });
        input.value = "";
      },
    });
  };

  const resetProductoForm = useCallback(() => {
    setTempProducto(createEmptyTempProducto());
    setResetProductoCombo(true);
  }, []);

  const handleInputChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = event.target;

    setTempProducto((prevState) => ({
      ...prevState,
      [name]: name === "cantidad" ? (value === "" ? undefined : value) : value,
    }));
  };

  const handleVentaChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = event.target;
    setVenta({ ...venta, [name]: value });
  };

  const donwloadTemplate = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/s3/signed-url?key=${encodeURIComponent("private/Template.xlsx")}`,
        { headers: { "auth-token": sessionStorage.getItem("token") } }
      );
      window.open(data.url, "_blank");
    } catch (err) {
      console.error(err);
    }
  };

  const getProductos = async () => {
    try {
      const response = await axios.get(
        `${
          process.env.REACT_APP_API_URL
        }/api/producto_farmacia/farmacia/${sessionStorage.getItem("user_id")}`,
        {
          headers: {
            "auth-token": `${sessionStorage.getItem("token")}`,
          },
        }
      );
      setProductList(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const removeProductById = (productoId: number, unidad: string) => {
    setSelectedProducts((prevProducts) =>
      prevProducts.filter(
        (product) =>
          !(product.producto_id === productoId && product.unidad === unidad)
      )
    );
  };

  const newProduct = () => {
    if (
      tempProducto.producto_id === undefined ||
      tempProducto.producto_id === 0 ||
      tempProducto.unidad === ""
    ) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Por favor, seleccione un producto.",
      });
      return;
    }

    const qty = Number(tempProducto.cantidad);
    if (!Number.isFinite(qty) || qty < 1) {
      Swal.fire({
        icon: "error",
        title: "Cantidad no válida",
        text: "Ingresá un número mayor o igual a 1.",
      });
      return;
    }

    const selectedProduct = productList.find(
      (product) => product.producto_id === tempProducto.producto_id
    );

    if (!selectedProduct) {
      console.error("Product not found!");
      return;
    }

    const existingProductIndex = selectedProducts.findIndex(
      (p) =>
        p.producto_id === tempProducto.producto_id &&
        p.unidad === tempProducto.unidad
    );

    if (existingProductIndex >= 0) {
      const newQuantity =
        Number(selectedProducts[existingProductIndex].cantidad) + qty;
      if (newQuantity < 1) {
        Swal.fire({
          icon: "error",
          title: "Cantidad no válida",
          text: "La suma por línea debe ser al menos 1.",
        });
        return;
      }
    } else if (!selectedProduct.articulo) {
      console.error("Product data is incomplete!");
      return;
    }

    setSelectedProducts((prevSelectedProducts) => {
      const existingIndex = prevSelectedProducts.findIndex(
        (p) =>
          p.producto_id === tempProducto.producto_id &&
          p.unidad === tempProducto.unidad
      );
      if (existingIndex >= 0) {
        const newQuantityType = tempProducto.unidad;
        const newQuantity =
          Number(prevSelectedProducts[existingIndex].cantidad) + qty;
        const updated = [...prevSelectedProducts];
        updated[existingIndex] = {
          ...updated[existingIndex],
          cantidad: newQuantity,
          articulo: updated[existingIndex].articulo,
          unidad: newQuantityType,
        };
        return updated;
      }
      return [
        ...prevSelectedProducts,
        {
          producto_id: selectedProduct.producto_id,
          nombre: selectedProduct.nombre,
          cantidad: qty,
          articulo: selectedProduct.articulo,
          unidad: tempProducto.unidad,
          cantidad_por_caja: selectedProduct.cantidad_por_caja,
        },
      ];
    });
    resetProductoForm();
  };

  function validateNumberLength(event: any) {
    const value = event.target.value;

    if (value.length > 12) {
      event.target.value = value.slice(0, 12);
    }

    if (value.length !== 12) {
      event.target.setCustomValidity(
        "El numero de factura ocupo 12 digitos, por favor verifique."
      );
    } else {
      event.target.setCustomValidity("");
    }
  }

  function isDateWithinThreeMonths(inputDate: any) {
    const date = new Date(inputDate);

    const currentDate = new Date();

    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(currentDate.getMonth() - 3);

    if (date < threeMonthsAgo) {
      return false;
    } else {
      return true;
    }
  }

  const closePanel = useCallback(() => {
    if (!view) {
      return;
    }
    setIsClosing(true);
    window.setTimeout(() => {
      setView(false);
      setIsClosing(false);
    }, 1000);
  }, [setView, view]);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const confirm = await Swal.fire({
      title: "¿Enviar el registro de venta?",
      text: "Se enviará la factura, el comprobante y las líneas de productos. ¿Continuar?",
      icon: "question",
      showCancelButton: true,
      focusCancel: true,
      confirmButtonText: "Sí, enviar",
      cancelButtonText: "No, volver",
      confirmButtonColor: BRAND,
    });
    if (!confirm.isConfirmed) {
      return;
    }

    try {
      if (
        selectedProducts.length === 0 ||
        !selectedFile ||
        !venta.numero_factura ||
        !venta.fecha
      ) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Por favor, complete todos los campos.",
          timer: 1700,
        });
        return;
      } else if (venta.numero_factura.toString().length !== 12) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "El número de factura debe tener 12 dígitos.",
          timer: 1700,
        });
        return;
      } else if (new Date(venta.fecha) < new Date("2024-07-15")) {
        Swal.fire({
          icon: "error",
          title: "Fecha no válida",
          text: "La fecha no puede ser anterior al 15 de julio de 2024.",
        });
        return;
      }

      // Do not await: Swal.fire() resolves on dismiss; showLoading() removes the
      // button so the promise would never resolve and upload/POST would never run.
      Swal.fire({
        title: "Procesando...",
        text: "No cierre esta ventana.",
        allowOutsideClick: false,
        showConfirmButton: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const currentTime = new Date();

      if (selectedFile) {
        const fileKey = `ventacomprobantes/${currentTime}-${selectedFile?.name}`;
        const { data: uploadData } = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/s3/upload-url`,
          { key: fileKey },
          { headers: { "auth-token": sessionStorage.getItem("token") } }
        );
        await axios.put(uploadData.url, selectedFile, { headers: { "Content-Type": selectedFile.type } });
      }

      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/venta_comprobante`,
        {
          venta: venta,
          productos: selectedProducts,
          file: `ventacomprobantes/${currentTime}-${selectedFile?.name}`,
          usuario_id: sessionStorage.getItem("user_id"),
          fecha_comprobante: venta.fecha,
        },
        {
          headers: {
            "Content-Type": "application/json",
            "auth-token": `${sessionStorage.getItem("token")}`,
          },
        }
      );

      Swal.close();
      void Swal.fire({
        icon: "success",
        title: "Éxito",
        text: "La venta se ha registrado correctamente.",
      });
      setVenta({ numero_factura: 0, fecha: new Date() });
      setSelectedProducts([]);
      setTempProducto(createEmptyTempProducto());
      setResetProductoCombo(true);
      setSelectedFile(undefined);
      setView(false);
    } catch (error) {
      Swal.close();
      if (axios.isAxiosError(error)) {
        if (error.response) {
          if (error.response.status === 404) {
            const unfoundProducts = error.response.data.unfoundProducts;

            showUnfoundProductsAlert(unfoundProducts);
          } else {
            Swal.fire({
              icon: "error",
              title: "Error",
              text:
                error.response.data.message ||
                "Ha ocurrido un error al enviar los datos.",
            });
          }
        } else if (error.request) {
          console.log("No response:", error.request);
          Swal.fire({
            icon: "error",
            title: "Error de Red",
            text: "No se pudo recibir una respuesta del servidor.",
          });
        } else {
          console.log("Error", error.message);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Error en la solicitud: " + error.message,
          });
        }
      } else {
        console.error("Error:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Un error inesperado ocurrió.",
        });
      }
    }
  };

  const showUnfoundProductsAlert = (unfoundProducts: any) => {
    const htmlContent = unfoundProducts
      .map((product: any) => `<tr><td>${product.nombre}</td></tr>`)
      .join("");

    Swal.fire({
      title: "Productos no encontrados",
      icon: "warning",
      html: `
        <p>Los siguientes productos no están disponibles:</p>
        <table class="table">
          <thead>
            <tr><th>Producto</th></tr>
          </thead>
          <tbody>
            ${htmlContent}
          </tbody>
        </table>
      `,
      confirmButtonText: "Aceptar",
    });
  };

  const productComboItems: ComboBoxItem[] = useMemo(
    () =>
      productList.map((p) => ({
        label: p.nombre,
        value: p.producto_id,
      })),
    [productList]
  );

  const applyProductoFromList = (foundProduct: Producto) => {
    if (foundProduct.cantidad_por_caja === 0) {
      setTempProducto((prevState) => ({
        ...prevState,
        unidad: "Caja",
        producto_id: foundProduct.producto_id,
        nombre: foundProduct.nombre,
        cantidad_por_caja: foundProduct.cantidad_por_caja,
      }));
    } else {
      setTempProducto((prevState) => ({
        ...prevState,
        nombre: foundProduct.nombre,
        producto_id: foundProduct.producto_id,
        cantidad_por_caja: foundProduct.cantidad_por_caja,
        unidad: "Unidad",
      }));
    }
  };

  const handleProductoComboSelect = (item: ComboBoxItem) => {
    const id = item.value as number;
    const foundProduct = productList.find((p) => p.producto_id === id);
    if (!foundProduct) {
      return;
    }
    applyProductoFromList(foundProduct);
  };

  useEffect(() => {
    if (view) {
      setTimeout(() => {
        setIsClosing(false);
      }, 1000);
    } else {
      setCsvReportOpen(false);
      setWizardStep(0);
    }
    getProductos();
  }, [view]);

  const fechaVentaDisplay = useMemo(() => {
    const f = venta.fecha as unknown;
    if (f instanceof Date && !Number.isNaN(f.getTime()) && f.getTime() !== 0) {
      return f.toLocaleDateString("es-CR", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    }
    if (typeof f === "string" && f.trim() !== "") {
      const d = new Date(f);
      if (!Number.isNaN(d.getTime())) {
        return d.toLocaleDateString("es-CR", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
      }
      return f;
    }
    return "—";
  }, [venta.fecha]);

  const goNextWizardStep = () => {
    if (wizardStep === 0) {
      const nf = String(venta.numero_factura ?? "").replace(/\D/g, "");
      if (nf.length !== 12) {
        void Swal.fire({
          icon: "warning",
          title: "Factura incompleta",
          text: "El número de factura debe tener 12 dígitos.",
          timer: 2000,
          showConfirmButton: false,
        });
        return;
      }
      if (!venta.fecha || new Date(venta.fecha).getTime() === 0) {
        void Swal.fire({
          icon: "warning",
          title: "Fecha requerida",
          text: "Seleccioná la fecha de la venta.",
          timer: 2000,
          showConfirmButton: false,
        });
        return;
      }
      if (new Date(venta.fecha) < new Date("2024-07-15")) {
        void Swal.fire({
          icon: "warning",
          title: "Fecha no válida",
          text: "La fecha no puede ser anterior al 15 de julio de 2024.",
        });
        return;
      }
      if (!selectedFile) {
        void Swal.fire({
          icon: "warning",
          title: "Comprobante requerido",
          text: "Subí la imagen del comprobante antes de continuar.",
          timer: 2200,
          showConfirmButton: false,
        });
        return;
      }
      setWizardStep(1);
      return;
    }
    if (wizardStep === 1) {
      if (selectedProducts.length === 0) {
        void Swal.fire({
          icon: "warning",
          title: "Sin productos",
          text: "Agregá al menos una línea de producto o importá un CSV.",
          timer: 2200,
          showConfirmButton: false,
        });
        return;
      }
      setWizardStep(2);
    }
  };

  const openFileSelector = () => {
    const fileInput = document.getElementById("csvInput") as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    } else {
      console.error("The file input element was not found.");
    }
  };

  return (
    <>
      {view || isClosing ? (
        <div className={view ? "bg-drawer" : "hidden"}>
          <div className="min-h-0 grow" aria-hidden />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="venta-comprobante-title"
            className={`absolute left-0 flex h-full max-h-screen w-screen min-w-full flex-col overflow-hidden bg-white shadow-[0_0_40px_rgba(15,23,42,0.12)] md:relative md:min-w-0 ${
              isClosing ? "animate-slide-out" : "animate-slide-in"
            }`}
            style={{ borderLeft: `1px solid ${BRAND_SOFT}` }}
          >
            <header
              className="flex shrink-0 items-center justify-between gap-3 border-b px-4 py-3 sm:px-5"
              style={{
                background: "linear-gradient(180deg, #f8faf9 0%, #fff 100%)",
                borderColor: BRAND_SOFT,
              }}
            >
              <div className="min-w-0">
                <h2
                  id="venta-comprobante-title"
                  className="text-lg font-bold tracking-tight text-slate-900 sm:text-xl"
                >
                  Registrar venta
                </h2>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                {wizardStep === 2 ? (
                  <button
                    type="submit"
                    form="venta-comp-form"
                    className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-white transition hover:brightness-110"
                    style={{ background: BRAND }}
                  >
                    <Send className="h-4 w-4" aria-hidden />
                    Enviar
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={closePanel}
                  className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
                  aria-label="Cerrar"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </header>

            <form
              id="venta-comp-form"
              onSubmit={(e) => {
                e.preventDefault();
                if (wizardStep !== 2) return;
                void handleSubmit(e);
              }}
              className="flex min-h-0 flex-1 flex-col"
            >
              <div
                className="shrink-0 border-b px-4 py-3 sm:px-5"
                style={{ borderColor: BRAND_SOFT, background: "#fafcfb" }}
              >
                <div className="flex gap-1.5" role="tablist" aria-label="Pasos del registro">
                  {VENTA_WIZARD_STEPS.map((_, i) => (
                    <div
                      key={i}
                      className="h-1.5 min-w-0 flex-1 rounded-full transition-[background] duration-300"
                      style={{
                        background: i <= wizardStep ? BRAND : "rgb(226 232 240)",
                      }}
                    />
                  ))}
                </div>
                <p className="mt-2 text-center text-xs font-medium text-slate-600">
                  {VENTA_WIZARD_STEPS[wizardStep].label}
                  <span className="font-normal text-slate-400">
                    {" "}
                    · Paso {wizardStep + 1} de {VENTA_WIZARD_STEPS.length}
                  </span>
                </p>
              </div>

              <div
                className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 py-4 sm:px-5"
                style={{ scrollbarGutter: "stable" }}
              >
                {wizardStep === 0 ? (
                <section
                  className="overflow-hidden rounded-2xl border bg-white shadow-sm"
                  style={{ borderColor: BRAND_SOFT }}
                >
                  <div className="space-y-4 p-4 sm:p-5">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <label
                          className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-slate-600"
                          htmlFor="numero_factura"
                        >
                          <Hash className="h-3.5 w-3.5" aria-hidden />
                          N° factura (12 dígitos fiscales)
                        </label>
                        <input
                          type="number"
                          id="numero_factura"
                          name="numero_factura"
                          onChange={handleVentaChange}
                          maxLength={12}
                          onInput={validateNumberLength}
                          className="main-input !h-11 border-slate-200/90 text-sm"
                          inputMode="numeric"
                          required
                        />
                        <p className="mt-1 text-[0.65rem] text-slate-400 sm:text-xs">
                          Últimos 12 dígitos del número de factura
                        </p>
                      </div>
                      <div>
                        <label
                          className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-slate-600"
                          htmlFor="fecha"
                        >
                          <Calendar className="h-3.5 w-3.5" aria-hidden />
                          Fecha de la venta
                        </label>
                        <input
                          type="date"
                          id="fecha"
                          name="fecha"
                          onChange={handleVentaChange}
                          className="main-input !h-11 border-slate-200/90 text-sm"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <p className="mb-2 text-xs font-medium text-slate-600 sm:text-sm">
                        Comprobante
                      </p>
                      <ImageUploader
                        setSelectedFile={setSelectedFile}
                        inputId="UsuarioVentaComprobante"
                        className="min-h-[140px] rounded-2xl border-2 border-dashed border-slate-200/90 bg-white shadow-sm"
                      />
                    </div>
                  </div>
                </section>
                ) : wizardStep === 1 ? (
                <>
                <section
                  className="overflow-hidden rounded-2xl border bg-white shadow-sm"
                  style={{ borderColor: BRAND_SOFT }}
                >
                  <div
                    className="flex items-center gap-2 border-b px-4 py-3"
                    style={{ borderColor: BRAND_SOFT, background: "#fafcfb" }}
                  >
                    <Plus
                      className="h-5 w-5 shrink-0"
                      style={{ color: BRAND }}
                      strokeWidth={2.5}
                      aria-hidden
                    />
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900">
                        Agregar productos
                      </h3>
                      <p className="text-xs text-slate-500">
                        Buscá por nombre, elegí unidad y cantidad
                      </p>
                    </div>
                  </div>
                  <div className="space-y-3 p-4 sm:p-5">
                    <div>
                      <ComboBox
                        label="Producto"
                        items={productComboItems}
                        placeholder="Escribí para buscar…"
                        onSelectItem={handleProductoComboSelect}
                        selectedValue={
                          tempProducto.producto_id
                            ? {
                                label: String(
                                  tempProducto.nombre ?? ""
                                ),
                                value: tempProducto.producto_id,
                              }
                            : null
                        }
                        resetInput={resetProductoCombo}
                        onResetInput={() => setResetProductoCombo(false)}
                        useFixedPosition
                        unlimitedOptions
                        showAddNew={false}
                        inputId="venta-combo-producto"
                      />
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div>
                        <label
                          className="mb-1.5 text-xs font-medium text-slate-600"
                          htmlFor="venta-unidad-dropdown"
                        >
                          Unidad
                        </label>
                        <Dropdown
                          id="venta-unidad-dropdown"
                          matchTriggerWidth
                          estimatedHeight={200}
                          triggerClassName="[&>div]:cursor-pointer"
                          menuClassName="!border-slate-200/90 !shadow-md"
                          trigger={({ open }) => (
                            <div className="main-input !flex !h-11 w-full !cursor-pointer items-center justify-between gap-2 !rounded-xl border !border-slate-200/90 !bg-white pl-3 pr-2 text-left text-sm text-slate-900">
                              <span className="min-w-0 truncate">
                                {tempProducto.unidad || "Caja"}
                              </span>
                              <ChevronDown
                                className={`h-4 w-4 shrink-0 text-slate-500 transition ${
                                  open ? "rotate-180" : ""
                                }`}
                                aria-hidden
                              />
                            </div>
                          )}
                        >
                          {({ pick }) => (
                            <div className="py-1" role="presentation">
                              <button
                                type="button"
                                role="option"
                                className="w-full px-3 py-2.5 text-left text-sm text-slate-800 transition hover:bg-slate-100"
                                onClick={() => {
                                  setTempProducto((p) => ({
                                    ...p,
                                    unidad: "Caja",
                                  }));
                                  pick();
                                }}
                              >
                                Caja
                              </button>
                              {tempProducto.cantidad_por_caja !== 0 && (
                                <button
                                  type="button"
                                  role="option"
                                  className="w-full px-3 py-2.5 text-left text-sm text-slate-800 transition hover:bg-slate-100"
                                  onClick={() => {
                                    setTempProducto((p) => ({
                                      ...p,
                                      unidad: "Unidad",
                                    }));
                                    pick();
                                  }}
                                >
                                  Unidad
                                </button>
                              )}
                            </div>
                          )}
                        </Dropdown>
                      </div>
                      <div>
                        <label
                          className="mb-1.5 text-xs font-medium text-slate-600"
                          htmlFor="cantidad"
                        >
                          Cantidad
                        </label>
                        <input
                          type="number"
                          id="cantidad"
                          name="cantidad"
                          value={
                            tempProducto.cantidad === undefined ||
                            tempProducto.cantidad === null
                              ? ""
                              : String(tempProducto.cantidad)
                          }
                          onChange={handleInputChange}
                          className="main-input !h-11 border-slate-200/90 text-sm"
                          min={1}
                          step="1"
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={newProduct}
                      className="flex w-full items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-semibold text-white transition hover:brightness-110"
                      style={{ background: BRAND }}
                    >
                      <Plus className="h-4 w-4" strokeWidth={2.5} />
                      Agregar a la venta
                    </button>
                  </div>
                </section>

                <section>
                  <div
                    className="mb-2 flex items-end justify-between gap-2"
                  >
                    <h3 className="text-sm font-semibold text-slate-900">
                      Líneas de la venta
                    </h3>
                    {selectedProducts.length > 0 ? (
                      <span className="text-xs text-slate-500">
                        {selectedProducts.length}{" "}
                        {selectedProducts.length === 1 ? "ítem" : "ítes"}
                      </span>
                    ) : null}
                  </div>
                  <div
                    className="max-h-[min(50vh,22rem)] space-y-2 overflow-y-auto pr-0.5"
                    style={{ scrollbarGutter: "stable" }}
                  >
                    {selectedProducts.length === 0 ? (
                      <div
                        className="rounded-2xl border border-dashed bg-slate-50/50 px-4 py-10 text-center text-sm text-slate-400"
                        style={{ borderColor: BRAND_SOFT }}
                      >
                        Aún no agregaste productos. Usá el formulario de arriba
                        o importá CSV.
                      </div>
                    ) : (
                      selectedProducts.map((producto) => (
                        <article
                          key={`${producto.producto_id}-${producto.unidad}`}
                          className="rounded-xl border bg-white p-3 shadow-sm"
                          style={{ borderColor: BRAND_SOFT }}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1 space-y-2">
                              <div>
                                <p className="text-[0.6rem] font-semibold uppercase tracking-wider text-slate-500">
                                  Artículo
                                </p>
                                <p className="break-words font-mono text-sm font-semibold text-slate-900">
                                  {producto.articulo}
                                </p>
                              </div>
                              <div>
                                <p className="text-[0.6rem] font-semibold uppercase tracking-wider text-slate-500">
                                  Nombre
                                </p>
                                <p className="text-sm leading-snug text-slate-700">
                                  {producto.nombre}
                                </p>
                              </div>
                              <div className="flex flex-wrap gap-x-4 gap-y-1 border-t border-slate-100 pt-2 text-sm">
                                <div>
                                  <span className="text-slate-500">Unidad: </span>
                                  <span className="font-medium text-slate-800">
                                    {producto.unidad}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-slate-500">Cant.: </span>
                                  <span className="font-medium text-slate-800">
                                    {producto.cantidad}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                removeProductById(
                                  producto.producto_id,
                                  producto.unidad
                                )
                              }
                              className="shrink-0 rounded-lg p-2 text-rose-600 transition hover:bg-rose-50"
                              aria-label="Quitar producto"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </article>
                      ))
                    )}
                  </div>
                </section>

                <section
                  className="overflow-hidden rounded-2xl border bg-slate-50/80 shadow-inner"
                  style={{ borderColor: BRAND_SOFT }}
                >
                  <button
                    type="button"
                    onClick={() => setCsvReportOpen((o) => !o)}
                    className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition hover:bg-slate-100/60 sm:px-5"
                    style={{ color: "inherit" }}
                    aria-expanded={csvReportOpen}
                    id="csv-report-toggle"
                  >
                    <span className="flex min-w-0 items-center gap-2.5">
                      <FileSpreadsheet
                        className="h-5 w-5 shrink-0"
                        style={{ color: BRAND }}
                        aria-hidden
                      />
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-slate-900">
                          Reporte (CSV)
                        </span>
                        <span className="mt-0.5 block text-xs text-slate-500">
                          {csvReportOpen
                            ? "Ocultar importación"
                            : "Desplegá para plantilla e importar archivo"}
                        </span>
                      </span>
                    </span>
                    <ChevronDown
                      className={`h-5 w-5 shrink-0 text-slate-400 transition ${
                        csvReportOpen ? "rotate-180" : ""
                      }`}
                      aria-hidden
                    />
                  </button>
                  <div
                    className="space-y-4 border-t p-4 sm:p-5"
                    style={{ borderColor: BRAND_SOFT }}
                    role="region"
                    aria-labelledby="csv-report-toggle"
                    hidden={!csvReportOpen}
                  >
                    <ol className="ml-0 list-decimal space-y-2 pl-4 text-sm text-slate-600">
                      <li>Descargá la plantilla oficial (Excel).</li>
                      <li>
                        Completá columnas según los datos de la pantalla{" "}
                        <strong>Productos</strong> de la app.
                      </li>
                      <li>
                        En el CSV, cada fila de datos debe tener las tres
                        columnas obligatorias: <strong>Articulo</strong>,{" "}
                        <strong>Unidad</strong> (solo <strong>Caja</strong> o{" "}
                        <strong>Unidad</strong>) y <strong>Cantidad</strong>{" "}
                        (entero ≥ 1). Si falta algo o el artículo no existe en
                        tu catálogo, no se importará el archivo.
                      </li>
                      <li>Exportá o guardá como CSV y subilo aquí abajo.</li>
                    </ol>
                    <button
                      type="button"
                      onClick={donwloadTemplate}
                      className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-200 bg-white py-2.5 text-sm font-medium text-slate-700 transition hover:border-[#205f43]/40 hover:bg-slate-50/80"
                    >
                      <FileDown className="h-4 w-4" />
                      Descargar plantilla
                    </button>
                    <button
                      type="button"
                      className="group flex w-full flex-col items-center justify-center gap-1 rounded-2xl border-2 border-dashed border-slate-200 bg-white py-5 text-sm text-slate-500 transition hover:border-[#205f43]/45 hover:text-slate-700"
                      onClick={openFileSelector}
                    >
                      <Upload
                        className="h-8 w-8 text-slate-300 transition group-hover:text-[#205f43]"
                        strokeWidth={1.5}
                      />
                      <span className="font-medium">Subir CSV</span>
                      <span className="text-xs text-slate-400">
                        o tocá para elegir archivo
                      </span>
                      <input
                        id="csvInput"
                        type="file"
                        accept=".csv"
                        className="hidden"
                        onChange={handleCSVUpload}
                      />
                    </button>
                  </div>
                </section>
                </>
                ) : (
                <section
                  className="overflow-hidden rounded-2xl border bg-white shadow-sm"
                  style={{ borderColor: BRAND_SOFT }}
                >
                  <div
                    className="border-b px-4 py-3 sm:px-5"
                    style={{ borderColor: BRAND_SOFT, background: "#fafcfb" }}
                  >
                    <h3 className="text-sm font-semibold text-slate-900">
                      Resumen del registro
                    </h3>
                    <p className="mt-0.5 text-xs text-slate-500">
                      Verificá los datos. Al enviar se sube el comprobante y se
                      registra la venta.
                    </p>
                  </div>
                  <div className="space-y-4 p-4 sm:p-5">
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="rounded-xl border border-slate-100 bg-slate-50/80 p-3">
                        <p className="text-[0.65rem] font-semibold uppercase tracking-wider text-slate-500">
                          N° factura
                        </p>
                        <p className="mt-1 font-mono text-base font-bold text-slate-900">
                          {String(venta.numero_factura ?? "").replace(/\D/g, "") ||
                            "—"}
                        </p>
                      </div>
                      <div className="rounded-xl border border-slate-100 bg-slate-50/80 p-3">
                        <p className="text-[0.65rem] font-semibold uppercase tracking-wider text-slate-500">
                          Fecha de venta
                        </p>
                        <p className="mt-1 text-base font-semibold text-slate-900">
                          {fechaVentaDisplay}
                        </p>
                      </div>
                    </div>
                    <div className="rounded-xl border border-slate-100 bg-slate-50/80 p-3">
                      <p className="text-[0.65rem] font-semibold uppercase tracking-wider text-slate-500">
                        Comprobante
                      </p>
                      <p className="mt-1 break-all text-sm font-medium text-slate-800">
                        {selectedFile?.name ?? "—"}
                      </p>
                    </div>
                    <div>
                      <p className="mb-2 text-xs font-semibold text-slate-700">
                        Productos ({selectedProducts.length})
                      </p>
                      <ul className="max-h-[min(40vh,16rem)] space-y-2 overflow-y-auto rounded-xl border border-slate-100 bg-white p-2">
                        {selectedProducts.map((p) => (
                          <li
                            key={`${p.producto_id}-${p.unidad}`}
                            className="rounded-lg border border-slate-100 px-3 py-2 text-sm"
                          >
                            <span className="font-medium text-slate-900">
                              {p.nombre}
                            </span>
                            <span className="mt-0.5 block text-xs text-slate-500">
                              {p.articulo} · {p.unidad} × {p.cantidad}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </section>
                )}
              </div>

              <footer
                className="shrink-0 border-t px-4 py-3 sm:px-5"
                style={{ borderColor: BRAND_SOFT, background: "#fafcfb" }}
              >
                <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
                  {wizardStep > 0 ? (
                    <button
                      type="button"
                      onClick={() => setWizardStep((s) => Math.max(0, s - 1))}
                      className="flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 sm:w-auto sm:min-w-[7.5rem]"
                    >
                      <ChevronLeft className="h-4 w-4" aria-hidden />
                      Atrás
                    </button>
                  ) : null}
                  {wizardStep < 2 ? (
                    <button
                      type="button"
                      onClick={goNextWizardStep}
                      className="flex w-full flex-1 items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold text-white transition hover:brightness-110 active:scale-[0.99] sm:py-2.5"
                      style={{ background: BRAND }}
                    >
                      Siguiente
                    </button>
                  ) : (
                    <button
                      type="submit"
                      className="flex w-full flex-1 items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold text-white transition hover:brightness-110 active:scale-[0.99] sm:py-2.5"
                      style={{ background: BRAND }}
                    >
                      <Send className="h-4 w-4" aria-hidden />
                      Enviar registro de venta
                    </button>
                  )}
                </div>
              </footer>
            </form>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default VentaComprobanteModal;
